'use client';

import * as React from 'react';
import { cn } from '@/lib/utils';
import { useBackgroundTheme } from './use-background-theme';

/**
 * VortexTunnel — Unendlicher hypnotischer Tunnel
 *
 * Technik: Canvas 2D, 30fps throttle, konzentrische Ringe mit Spiral-Rotation
 * CPU-Impact: Low (~20 Ringe, einfache Geometrie)
 *
 * Verwendung:
 *   <VortexTunnelBackground>
 *     <div className="relative z-10">Content</div>
 *   </VortexTunnelBackground>
 */
function VortexTunnelBackground({
  className,
  children,
  speed = 1,
  opacity = 1,
  color1,
  color2,
  ringCount = 20,
  twist = 0.3,
  variant = 'auto',
  ...props
}) {
  const isDark = useBackgroundTheme(variant);
  const resolvedColor1 = color1 ?? (isDark ? '#06b6d4' : '#0284c7');
  const resolvedColor2 = color2 ?? (isDark ? '#8b5cf6' : '#7c3aed');
  const bgClass = isDark ? 'bg-slate-950' : 'bg-white';
  const canvasRef = React.useRef(null);
  const animRef = React.useRef(null);
  const lastFrameRef = React.useRef(0);
  const offsetRef = React.useRef(0);
  const pausedRef = React.useRef(false);
  const FPS = 30;
  const INTERVAL = 1000 / FPS;

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { willReadFrequently: false });

    let w = canvas.offsetWidth;
    let h = canvas.offsetHeight;
    canvas.width = w;
    canvas.height = h;

    const observer = new IntersectionObserver(
      ([entry]) => { pausedRef.current = !entry.isIntersecting; },
      { threshold: 0.01 },
    );
    observer.observe(canvas);

    const ro = new ResizeObserver(() => {
      w = canvas.offsetWidth;
      h = canvas.offsetHeight;
      canvas.width = w;
      canvas.height = h;
    });
    ro.observe(canvas);

    // Parse colors
    const hexToRgb = (hex) => {
      const h = hex.replace('#', '');
      return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
    };
    const [r1, g1, b1] = hexToRgb(resolvedColor1);
    const [r2, g2, b2] = hexToRgb(resolvedColor2);

    const draw = (timestamp) => {
      animRef.current = requestAnimationFrame(draw);
      if (pausedRef.current) return;
      if (timestamp - lastFrameRef.current < INTERVAL) return;
      lastFrameRef.current = timestamp;

      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

      offsetRef.current += 0.015 * speed;
      const t = offsetRef.current;

      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = isDark ? 'rgb(2,6,23)' : 'rgb(255,255,255)';
      ctx.fillRect(0, 0, w, h);

      const cx = w / 2;
      const cy = h / 2;
      const maxR = Math.max(w, h) * 0.7;

      for (let i = ringCount; i >= 0; i--) {
        // Ring moves toward viewer: use fract(i/ringCount + t)
        const fraction = ((i / ringCount) + t) % 1;
        const radius = fraction * maxR;
        // Near rings are more opaque; far rings are transparent
        const near = fraction; // 0=far, 1=near
        const ringOpacity = near * near * 0.8 * opacity;

        if (radius < 1) continue;

        // Alternate colors
        const mix = (Math.sin(i * 0.8 + t * 2) + 1) / 2;
        const r = Math.round(r1 + (r2 - r1) * mix);
        const g = Math.round(g1 + (g2 - g1) * mix);
        const b = Math.round(b1 + (b2 - b1) * mix);

        // Rotation for spiral effect
        const rotation = i * twist + t * 1.5;

        // Draw as polygon instead of circle for crystal look
        const sides = 6 + (i % 3); // 6, 7, or 8 sides
        ctx.beginPath();
        for (let s = 0; s <= sides; s++) {
          const angle = (s / sides) * Math.PI * 2 + rotation;
          // Slight wobble for organic feel
          const wobble = 1 + 0.04 * Math.sin(s * 1.3 + t * 3);
          const x = cx + Math.cos(angle) * radius * wobble;
          const y = cy + Math.sin(angle) * radius * wobble;
          if (s === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.closePath();

        const lineWidth = Math.max(0.5, near * 2.5);
        ctx.strokeStyle = `rgba(${r},${g},${b},${ringOpacity})`;
        ctx.lineWidth = lineWidth;
        ctx.stroke();

        // Glow for near rings
        if (near > 0.6) {
          ctx.strokeStyle = `rgba(${r},${g},${b},${(near - 0.6) * 0.3 * opacity})`;
          ctx.lineWidth = lineWidth * 4;
          ctx.stroke();
        }
      }

      // Center glow
      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, 60);
      grad.addColorStop(0, `rgba(${r1},${g1},${b1},${(isDark ? 0.15 : 0.08) * opacity})`);
      grad.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(cx, cy, 60, 0, Math.PI * 2);
      ctx.fill();
    };

    animRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(animRef.current);
      observer.disconnect();
      ro.disconnect();
    };
  }, [speed, resolvedColor1, resolvedColor2, ringCount, twist, opacity, isDark]);

  return (
    <div
      data-slot="vortex-tunnel-background"
      className={cn('relative size-full overflow-hidden', bgClass, className)}
      {...props}
    >
      <canvas ref={canvasRef} className="absolute inset-0 size-full" />
      {children && (
        <div className="relative z-10 size-full">{children}</div>
      )}
    </div>
  );
}

export { VortexTunnelBackground };
